/**
 * Storage Service - IndexedDB for samples, localStorage for settings
 * Provides sample library and preset management
 */

import type { LayerType } from '../types';

// Sample metadata stored in IndexedDB
export interface StoredSample {
  id: string;
  name: string;
  type: string;
  size: number;
  duration: number;
  createdAt: number;
  data: ArrayBuffer;
}

// Layer settings
export interface LayerSettings {
  muted: boolean;
  volume: number;
}

// Full preset/settings structure
export interface GenMusPreset {
  id: string;
  name: string;
  createdAt: number;
  sampleId?: string; // Reference to stored sample
  params: {
    tempo: number;
    density: number;
    mood: number;
    reverb: number;
    delay: number;
    compression?: number; // 0-100, defaults to 30
    saturation?: number; // 0-100, defaults to 0
    smoothness: number;
    master: number;
  };
  layers: Record<LayerType, LayerSettings>;
}

const DB_NAME = 'genmus-db';
const DB_VERSION = 1;
const SAMPLES_STORE = 'samples';
const PRESETS_KEY = 'genmus-presets';
const CURRENT_SETTINGS_KEY = 'genmus-current-settings';

/**
 * Initialize IndexedDB
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Create samples object store
      if (!db.objectStoreNames.contains(SAMPLES_STORE)) {
        const store = db.createObjectStore(SAMPLES_STORE, { keyPath: 'id' });
        store.createIndex('name', 'name', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
  });
}

/**
 * Generate unique ID
 */
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

// ============= SAMPLE STORAGE =============

/**
 * Save a sample to IndexedDB
 */
export async function saveSample(file: File, duration: number): Promise<StoredSample> {
  const db = await openDB();
  const arrayBuffer = await file.arrayBuffer();
  
  const sample: StoredSample = {
    id: generateId(),
    name: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
    type: file.type,
    size: file.size,
    duration,
    createdAt: Date.now(),
    data: arrayBuffer,
  };
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SAMPLES_STORE, 'readwrite');
    const store = tx.objectStore(SAMPLES_STORE);
    const request = store.add(sample);
    
    request.onsuccess = () => resolve(sample);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all stored samples (metadata only, without data)
 */
export async function getSampleList(): Promise<Omit<StoredSample, 'data'>[]> {
  const db = await openDB();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SAMPLES_STORE, 'readonly');
    const store = tx.objectStore(SAMPLES_STORE);
    const request = store.getAll();
    
    request.onsuccess = () => {
      // Return metadata without the heavy ArrayBuffer data
      const samples = request.result.map(({ data, ...meta }) => meta);
      resolve(samples);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get a specific sample by ID
 */
export async function getSample(id: string): Promise<StoredSample | null> {
  const db = await openDB();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SAMPLES_STORE, 'readonly');
    const store = tx.objectStore(SAMPLES_STORE);
    const request = store.get(id);
    
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete a sample by ID
 */
export async function deleteSample(id: string): Promise<void> {
  const db = await openDB();
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SAMPLES_STORE, 'readwrite');
    const store = tx.objectStore(SAMPLES_STORE);
    const request = store.delete(id);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Convert stored sample to File object for loading
 */
export function sampleToFile(sample: StoredSample): File {
  const blob = new Blob([sample.data], { type: sample.type });
  return new File([blob], sample.name, { type: sample.type });
}

// ============= SETTINGS/PRESET STORAGE =============

/**
 * Get current settings from localStorage
 */
export function getCurrentSettings(): Partial<GenMusPreset> | null {
  try {
    const data = localStorage.getItem(CURRENT_SETTINGS_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

/**
 * Save current settings to localStorage
 */
export function saveCurrentSettings(settings: Partial<GenMusPreset>): void {
  try {
    localStorage.setItem(CURRENT_SETTINGS_KEY, JSON.stringify(settings));
  } catch (e) {
    console.error('Failed to save settings:', e);
  }
}

/**
 * Get all saved presets
 */
export function getPresets(): GenMusPreset[] {
  try {
    const data = localStorage.getItem(PRESETS_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

/**
 * Save a new preset
 */
export function savePreset(preset: Omit<GenMusPreset, 'id' | 'createdAt'>): GenMusPreset {
  const presets = getPresets();
  
  const newPreset: GenMusPreset = {
    ...preset,
    id: generateId(),
    createdAt: Date.now(),
  };
  
  presets.push(newPreset);
  localStorage.setItem(PRESETS_KEY, JSON.stringify(presets));
  
  return newPreset;
}

/**
 * Delete a preset by ID
 */
export function deletePreset(id: string): void {
  const presets = getPresets().filter(p => p.id !== id);
  localStorage.setItem(PRESETS_KEY, JSON.stringify(presets));
}

/**
 * Get a preset by ID
 */
export function getPreset(id: string): GenMusPreset | null {
  return getPresets().find(p => p.id === id) || null;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

/**
 * Format duration for display
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
